import { BackendErrorPayload } from '@paywithextend/components/dist/error.utils'
import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import {
  AttachmentConnectedPlatformListResponse,
  ReceiptAttachmentResponse,
} from 'src/models/receiptAttachment.models'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAuthedFormApiRequestConfig,
  selectAuthedJsonApiRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'

export const UPLOAD_ATTACHMENT_INIT =
  'data/receiptAttachments/UPLOAD_ATTACHMENT_INIT'
export const UPLOAD_ATTACHMENT_SUCCESS =
  'data/receiptAttachments/UPLOAD_ATTACHMENT_SUCCESS'
export const UPLOAD_ATTACHMENT_FAILURE =
  'data/receiptAttachments/UPLOAD_ATTACHMENT_FAILURE'

export type UploadAttachmentInitPayload = {
  uploadId: string
  thumbnailUrl: string
  fileName: string
}
export type UploadAttachmentSuccessPayload = ReceiptAttachmentResponse & {
  uploadId: string
}
export type UploadAttachmentFailurePayload = {
  uploadId: string
}
export interface UploadAttachmentInitAction {
  type: typeof UPLOAD_ATTACHMENT_INIT
  payload: UploadAttachmentInitPayload
}
export interface UploadAttachmentSuccessAction {
  type: typeof UPLOAD_ATTACHMENT_SUCCESS
  payload: UploadAttachmentSuccessPayload
}
export interface UploadAttachmentFailureAction {
  type: typeof UPLOAD_ATTACHMENT_FAILURE
  payload: UploadAttachmentFailurePayload
}

export type UploadAttachmentActions =
  | UploadAttachmentInitAction
  | UploadAttachmentSuccessAction
  | UploadAttachmentFailureAction

const createUploadAttachmentInitAction = (
  payload: UploadAttachmentInitPayload,
): UploadAttachmentInitAction => ({
  type: UPLOAD_ATTACHMENT_INIT,
  payload,
})
const createUploadAttachmentSuccessAction = (
  payload: UploadAttachmentSuccessPayload,
): UploadAttachmentSuccessAction => ({
  type: UPLOAD_ATTACHMENT_SUCCESS,
  payload,
})
const createUploadAttachmentFailureAction = (
  payload: UploadAttachmentFailurePayload,
): UploadAttachmentFailureAction => ({
  type: UPLOAD_ATTACHMENT_FAILURE,
  payload,
})

export const uploadAttachment = (
  formData: FormData,
  uploadId: string,
  thumbnailUrl: string,
  fileName: string,
): ThunkAction<
  Promise<UploadAttachmentFailureAction | UploadAttachmentSuccessAction>,
  RootState,
  void,
  UploadAttachmentActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, UploadAttachmentActions>,
    getState: () => RootState,
  ): Promise<UploadAttachmentFailureAction | UploadAttachmentSuccessAction> => {
    dispatch(
      createUploadAttachmentInitAction({ uploadId, thumbnailUrl, fileName }),
    )
    return axios
      .post(
        `/receiptattachments`,
        formData,
        selectAuthedFormApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          return dispatch(
            createUploadAttachmentSuccessAction({ ...response.data, uploadId }),
          )
        },
        (err) => {
          return dispatch(
            createUploadAttachmentFailureAction({ ...err.response, uploadId }),
          )
        },
      )
  }
}

export const uploadAttachments = (
  attachments: PostReceiptAttachmentRequest[],
): ThunkAction<
  Promise<Array<UploadAttachmentFailureAction | UploadAttachmentSuccessAction>>,
  RootState,
  void,
  UploadAttachmentActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, UploadAttachmentActions>,
  ): Promise<
    Array<UploadAttachmentFailureAction | UploadAttachmentSuccessAction>
  > => {
    const promises = attachments.map((att) =>
      dispatch(
        uploadAttachment(
          att.formData,
          att.uploadId,
          att.thumbnailUrl,
          att.fileName,
        ),
      ),
    )
    return Promise.all(promises)
  }
}

export const POST_ERP_RECEIPT_ATTACHMENTS_INIT =
  'data/receiptAttachments/POST_ERP_RECEIPT_ATTACHMENTS_INIT'
export const POST_ERP_RECEIPT_ATTACHMENTS_SUCCESS =
  'data/receiptAttachments/POST_ERP_RECEIPT_ATTACHMENTS_SUCCESS'
export const POST_ERP_RECEIPT_ATTACHMENTS_FAILURE =
  'data/receiptAttachments/POST_ERP_RECEIPT_ATTACHMENTS_FAILURE'

export type PostERPReceiptAttachmentsSuccessPayload = {
  transactionId: string
  receiptAttachments: AttachmentConnectedPlatformListResponse[]
}
export type PostERPReceiptAttachmentsFailurePayload = {
  transactionId: string
  errors: BackendErrorPayload<string>
}

export interface PostERPReceiptAttachmentsInitAction {
  type: typeof POST_ERP_RECEIPT_ATTACHMENTS_INIT
}
export interface PostERPReceiptAttachmentsSuccessAction {
  type: typeof POST_ERP_RECEIPT_ATTACHMENTS_SUCCESS
  payload: PostERPReceiptAttachmentsSuccessPayload
}
export interface PostERPReceiptAttachmentsFailureAction {
  type: typeof POST_ERP_RECEIPT_ATTACHMENTS_FAILURE
  payload: PostERPReceiptAttachmentsFailurePayload
}

export type PostERPReceiptAttachmentsActions =
  | PostERPReceiptAttachmentsInitAction
  | PostERPReceiptAttachmentsSuccessAction
  | PostERPReceiptAttachmentsFailureAction

const createPostERPReceiptAttachmentsInitAction =
  (): PostERPReceiptAttachmentsInitAction => ({
    type: POST_ERP_RECEIPT_ATTACHMENTS_INIT,
  })
const createPostERPReceiptAttachmentsSuccessAction = (
  payload: PostERPReceiptAttachmentsSuccessPayload,
): PostERPReceiptAttachmentsSuccessAction => ({
  type: POST_ERP_RECEIPT_ATTACHMENTS_SUCCESS,
  payload,
})
const createPostERPReceiptAttachmentsFailureAction = (
  payload: PostERPReceiptAttachmentsFailurePayload,
): PostERPReceiptAttachmentsFailureAction => ({
  type: POST_ERP_RECEIPT_ATTACHMENTS_FAILURE,
  payload,
})

export const postERPReceiptAttachments = (
  orgId: string,
  connectionId: string,
  transactionId: string,
): ThunkAction<
  Promise<
    | PostERPReceiptAttachmentsFailureAction
    | PostERPReceiptAttachmentsSuccessAction
  >,
  RootState,
  void,
  PostERPReceiptAttachmentsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostERPReceiptAttachmentsActions>,
    getState: () => RootState,
  ): Promise<
    | PostERPReceiptAttachmentsFailureAction
    | PostERPReceiptAttachmentsSuccessAction
  > => {
    dispatch(createPostERPReceiptAttachmentsInitAction())
    return axios
      .post(
        `/organizations/${orgId}/connections/${connectionId}/attachments/push`,
        { transactionId: transactionId },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          return dispatch(
            createPostERPReceiptAttachmentsSuccessAction({
              transactionId: transactionId,
              receiptAttachments: response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createPostERPReceiptAttachmentsFailureAction({
              transactionId: transactionId,
              errors: err.response,
            }),
          )
        },
      )
  }
}
